// src/components/AllocationChart.jsx
import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const MARKET_COLORS = {
  US: ["#1a1a2e", "#16213e", "#0f3460", "#533483", "#2b2d42", "#8d99ae", "#3a3a5c", "#4a4a7a", "#6a6a9a", "#9a9aba"],
  TW: ["#2d6a4f", "#40916c", "#52b788", "#74c69d", "#95d5b2", "#b7e4c7"],
  JP: ["#9b2335", "#c0392b", "#e74c3c", "#e57373", "#ef9a9a", "#ffcdd2"],
  HK: ["#b45309", "#d97706", "#f59e0b", "#fbbf24", "#fcd34d", "#fde68a"],
};

function getColor(market, index) {
  const colors = MARKET_COLORS[market] || MARKET_COLORS.US;
  return colors[index % colors.length];
}

export default function AllocationChart({ holdings, marketData }) {
  const [groupBy, setGroupBy] = useState("ticker");

  const totalValueUSD = holdings.reduce((sum, h) => {
    const md = marketData[h.ticker];
    return sum + (md ? md.priceUSD * h.quantity : 0);
  }, 0);

  let chartData;
  if (groupBy === "ticker") {
    chartData = holdings
      .map((h, i) => {
        const md = marketData[h.ticker];
        if (!md) return null;
        const value = md.priceUSD * h.quantity;
        return {
          name: h.name || h.ticker,
          value,
          pct: totalValueUSD > 0 ? (value / totalValueUSD) * 100 : 0,
          color: getColor(h.market, i),
          market: h.market,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value);
  } else {
    const byMarket = {};
    holdings.forEach(h => {
      const md = marketData[h.ticker];
      if (!md) return;
      const val = md.priceUSD * h.quantity;
      byMarket[h.market] = (byMarket[h.market] || 0) + val;
    });
    chartData = Object.entries(byMarket).map(([market, value]) => ({
      name: market,
      value,
      pct: totalValueUSD > 0 ? (value / totalValueUSD) * 100 : 0,
      color: getColor(market, 0),
      market,
    }));
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
        <div style={{ fontWeight: 700 }}>{d.name}</div>
        <div style={{ color: "#555" }}>${d.value.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
        <div style={{ color: "#888" }}>{d.pct.toFixed(2)}%</div>
      </div>
    );
  };

  const legendItems = chartData;

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>비중 분포</h3>
        <div style={{ display: "flex", gap: 6 }}>
          {["ticker", "market"].map(opt => (
            <button key={opt} onClick={() => setGroupBy(opt)} style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid #e8e8e8",
              background: groupBy === opt ? "#1a1a2e" : "#fff",
              color: groupBy === opt ? "#fff" : "#555",
              fontSize: 11, cursor: "pointer", fontWeight: 500,
            }}>
              {opt === "ticker" ? "종목별" : "마켓별"}
            </button>
          ))}
        </div>
      </div>

      {chartData.length === 0 ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: 40 }}>데이터 없음</div>
      ) : (
        <>
          {/* 도넛 차트 — Legend 없이 */}
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%" cy="50%"
                innerRadius={65} outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* 범례 — 차트 아래 별도 렌더링 */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px 12px",
            marginTop: 12,
            maxHeight: 320,
            overflowY: "auto",
          }}>
            {legendItems.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.name} <span style={{ color: "#aaa" }}>({item.pct.toFixed(1)}%)</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
